import { ReactNode } from 'react';
import './DashboardSideNav.scss';
import logoImage from '../../../../assets/images/branding/gg_navbar_brand_orange.png';
import thumbnailLogo from '../../../../assets/images/branding/gg_thumbnail.png';
import platinumSparkLogo from '../../../../assets/images/branding/PlatinumSpark_Logo_favicon.png';
import { Link } from 'react-router-dom';
import { Avatar, Box, Typography } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SellerInterface } from '../../../../Models/Seller';
import GGButton from '../../../CommonComponents/GGButton/GGButton';
import { PremiumPlanBox } from '../PremiumPlanBox/PremiumPlanBox';

type DashboardNavItem<K extends string = string> = {
    key: K;
    label: string;
    icon?: ReactNode;
    type: 'primary' | 'secondary';
};

type NewDashboardSideNavProps<K extends string = string> = {
    items: DashboardNavItem<K>[];
    activeItem: K;
    onSelect: (key: K) => void;
    seller?: SellerInterface;
    onProfileMenuOpen?: (event: React.MouseEvent<HTMLElement>) => void;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    onPlanBoxClick?: () => void;
};

export const DashboardSideNav = <K extends string = string>({
    items,
    activeItem,
    onSelect,
    seller,
    onProfileMenuOpen,
    isCollapsed = false,
    onToggleCollapse,
    onPlanBoxClick,
}: NewDashboardSideNavProps<K>) => {
    return (
        <nav className={`new-dashboard-side-nav-container ${isCollapsed ? 'collapsed' : ''}`} aria-label="Seller dashboard navigation">
            <div className="new-dashboard-side-nav-header-wrapper">
                <Link to="/" title="Go to Home">
                    <div className="new-dashboard-side-nav-header">
                        <img src={isCollapsed ? thumbnailLogo : logoImage} alt="logo" />
                        <hr className="new-dashboard-side-nav-header-divider" />
                    </div>
                </Link>

                {seller && onProfileMenuOpen && (
                    <div className="mobile-user-profile-section">
                        <GGButton 
                            disableClickShrink={true} 
                            type="button" 
                            className="mobile-user-profile-button" 
                            aria-label="User profile menu"
                            onClick={onProfileMenuOpen}
                        >
                            <div className="mobile-user-profile-avatar">
                                {seller?.profileImage ? (
                                    <Avatar src={seller.profileImage} alt="User Profile" sx={{ width: 40, height: 40 }} />
                                ) : (
                                    <Skeleton variant="circular" animation="wave" sx={{ width: 40, height: 40 }} />
                                )}
                            </div>
                            <Typography className="mobile-user-profile-name">
                                {seller?.fullName || "Loading..."}
                            </Typography>
                            <KeyboardArrowDownIcon className="mobile-user-profile-dropdown-icon" />
                        </GGButton>
                    </div>
                )}
            </div>

            <div className="new-dashboard-side-nav-content">
            {/* Premium Plan Box */}
            {seller?.planDetails && !isCollapsed && onPlanBoxClick && (
                <Box paddingTop={"4px"}>
                    <PremiumPlanBox 
                        planDetails={seller.planDetails}
                        onClick={onPlanBoxClick}
                    />
                </Box>
            )}

            <ul className="new-dashboard-side-nav-list">
                {items.filter((item) => item.type === 'primary').map((item) => (
                    <li key={item.key} className={`nav-item ${activeItem === item.key ? 'active' : ''}`}>
                        <button
                            type="button"
                            className="nav-item-button"
                            onClick={() => onSelect(item.key)}
                            aria-current={activeItem === item.key ? 'page' : undefined}
                        >
                            {item.icon && <span className="nav-item-icon">{item.icon}</span>}
                            <span className="nav-item-label">{item.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
            </div>
            
            <div className="new-dashboard-side-nav-secondary-wrapper">
            <ul className="new-dashboard-side-nav-list secondary-list">
                {items.filter((item) => item.type === 'secondary').map((item) => (
                    <li key={item.key} className={`nav-item ${activeItem === item.key ? 'active' : ''}`}>
                        <button
                            type="button"
                            className="nav-item-button"
                            onClick={() => onSelect(item.key)}
                            aria-current={activeItem === item.key ? 'page' : undefined}
                        >
                            {item.icon && <span className="nav-item-icon">{item.icon}</span>}
                            <span className="nav-item-label">{item.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
            </div>

            <div className="new-dashboard-side-nav-footer-wrapper">
            <div className="new-dashboard-side-nav-footer">
                {!isCollapsed && (
                    <>
                        <div className="new-dashboard-side-nav-footer-platinum-spark-container">
                            <img  src={platinumSparkLogo} alt="logo" />
                            <p>PlatinumSpark Technologies Pvt Ltd 2025</p>
                        </div>
                        <p>© All rights reserved.</p>
                    </>
                )}
            </div>
            </div>

        </nav>
    );
};